import { useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useBulkDeleteFacts } from '@/api/mutations/useBulkDeleteFacts';
import { useIndexedDb } from '@/hooks/useIndexedDb';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { InfoCard } from '@/new/components/info-card';
import { DropdownMenuItem } from '@/new/components/shadcn/dropdown-menu';

function archiveLabel(count: number) {
  return `Archive ${count > 0 ? `${count} ` : ''}Fact${count === 1 ? '' : 's'}`;
}

export function ArchiveSelectedMenuItem({
  selectedFactIds,
  onSelect,
}: {
  selectedFactIds: Set<string>;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      disabled={selectedFactIds.size === 0}
      onSelect={(e) => {
        e.preventDefault();
        onSelect();
      }}
    >
      <Icon className="lucide--archive" />
      {archiveLabel(selectedFactIds.size)}
    </DropdownMenuItem>
  );
}

export function ArchiveSelectedDialog({
  open,
  onOpenChange,
  selectedFactIds,
  setSelectedFactIds,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFactIds: Set<string>;
  setSelectedFactIds: (ids: Set<string>) => void;
  onSuccess?: () => void;
}) {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const { getAllSelectedFactIds, deselectMultipleFacts } = useIndexedDb();
  const { mutate: deleteFacts, isLoading: isDeletingFacts } = useBulkDeleteFacts(matterId, subsetId);

  const handleDeleteAll = async () => {
    const factIds = await getAllSelectedFactIds(subsetId);
    trackCustomEvent(TrackingEventsV2.FactBulkDelete, { subsetId, count: factIds.length });
    deleteFacts(
      { factIds },
      {
        onSuccess: () => {
          deselectMultipleFacts(subsetId, factIds);
          setSelectedFactIds(new Set());
          onOpenChange(false);
          onSuccess?.();
        },
      },
    );
  };

  return (
    <ConfirmationModal
      isOpen={open}
      onOpenChange={onOpenChange}
      title="Archive Facts"
      onConfirm={handleDeleteAll}
      isLoading={isDeletingFacts}
    >
      <InfoCard className="mb-4 text-sm text-gray-600">
        <p>Large archives may take a moment to appear in the facts table.</p>
      </InfoCard>
      <p className="mt-3">
        Are you sure you want to archive all selected facts? This will prevent these facts from appearing in any subset.
      </p>
      <p className="mt-3">Note that any references to these facts will still exist.</p>
    </ConfirmationModal>
  );
}
